/**
 * Dispatching a whole grading WAVE — all three phases, from one scope, in one place.
 *
 * WHY THIS EXISTS. The `grading` step has three job types with three different units: `grading` per (main task ×
 * submission), `gradingConsistency` per main task, and `languageUseGroupGrading` per (submission × multi-member
 * language-use group). Only the first used to be dispatched; the other two were enqueued from inside the grading
 * handler once a run had discovered them. That worked only because a per-submission lock made the phases strictly
 * sequential, and it cost three things:
 *
 *  - **a phase could be graded twice.** A dedupe key is held only while a row is LIVE, so as soon as a group row
 *    could finish before a sibling content row — which is exactly what a per-task lock allows — that row's
 *    follow-up insert found no twin and created a second one.
 *  - **a caller could not wait for the wave.** The follow-up rows' ids never reached whoever dispatched, so the
 *    chat tool's "Bewertung abgeschlossen" fired as soon as the CONTENT rows were done, while language-use
 *    grading was still running.
 *  - **the canvas could not show the wave.** A language-use box had no live row to derive a spinner from until
 *    every content row had finished, while every content box lit up at once.
 *
 * Dispatching all three up front fixes all three, and costs nothing in ordering: `gradingConsistency` already
 * gates itself on "no live `grading` row for my task", so it waits in the queue rather than in a callback.
 *
 * ONE BATCH, ONE ANNOUNCEMENT. `batchScopeOf` maps all three types to the step name `grading`, and
 * `openOrJoinBatch` is a compare-and-set on `examState.batchKeys.grading` — so the three dispatches below join
 * one batch and the wave still announces exactly once when the last of its rows goes terminal.
 */

import { logger } from '@adornis/base/logging.js';
import type { BatchIntent } from './batch-store.js';
import { dispatchJobs } from './dispatch.js';
import type { GradingPayload, GradingScope } from './types/grading.job.js';

export interface GradingWaveDispatch {
  /** Every row of the wave — what a caller waits ON if it wants the whole thing, not just the content pass. */
  jobIds: string[];
  /** The content rows' payloads, in enumeration order — the caller's answer to "which tasks did I just start?" */
  payloads: GradingPayload[];
}

/**
 * Dispatches one grading wave and returns every row it created.
 *
 * ORDER IS DELIBERATE, and each step depends on the one before it:
 *
 *  1. `grading` first, because the content run is what OPENS the progress record, and because its enumeration is
 *     what tells us which main tasks the wave actually covers.
 *  2. `languageUseGroupGrading` second. It enumerates itself from the exam, so it needs nothing from step 1 — but
 *     it must not go first, or a wave whose content rows all dedupe away would be left with group rows alone.
 *  3. `gradingConsistency` last, and it has to be last: its `ready()` gate holds only while a live `grading` row
 *     shares its `taskKey`, so a consistency row created before the content rows exist would claim immediately
 *     and skip with nothing to reconcile.
 *
 * The tasks handed to step 3 are the payloads step 1 produced rather than a second enumeration from `taskRefs`:
 * `groupTaskRefsByMainTask` already did that work, and doing it twice is two implementations that can drift.
 */
export async function dispatchGradingWave(
  examId: string,
  scope: GradingScope,
  intent?: BatchIntent,
): Promise<GradingWaveDispatch> {
  const content = await dispatchJobs<GradingPayload>('grading', examId, scope, intent);

  const languageUse = await dispatchJobs('languageUseGroupGrading', examId, scope, intent);

  // One entry per main task, carrying that task's own refs — `groupTaskRefsByMainTask` already split the
  // request per task, and every submission's row for a given task shares them, so the first payload per
  // `taskKey` is representative.
  const consistencyTasks = [
    ...new Map(
      content.payloads.map(payload => [
        payload.taskKey,
        {
          taskKey: payload.taskKey,
          taskNumber: payload.taskNumber,
          ...(payload.taskRefs?.length ? { taskRefs: payload.taskRefs } : {}),
        },
      ]),
    ).values(),
  ];
  const consistency = consistencyTasks.length
    ? await dispatchJobs('gradingConsistency', examId, { ...scope, consistencyTasks }, intent)
    : { jobIds: [], payloads: [] };

  logger.info(
    {
      examId,
      content: content.jobIds.length,
      languageUse: languageUse.jobIds.length,
      consistency: consistency.jobIds.length,
    },
    'Dispatched a grading wave',
  );

  return {
    jobIds: [...content.jobIds, ...languageUse.jobIds, ...consistency.jobIds],
    payloads: content.payloads,
  };
}
